import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router, ActivatedRoute } from '@angular/router';
import { map, catchError, switchMap } from 'rxjs/operators';
import { User } from '../../models/user.model';
import { of, BehaviorSubject, throwError, Observable } from 'rxjs';
import { environment } from 'environments/environment';
import { SessionStorageService } from '../session-storage.service';
import { config } from 'config';
import { ProfileType, ProfileTypeId, UserRole } from 'app/shared/enums/profile-type.enum';
import { TotpService } from '../totp.service';

export interface UserRoleResponse {
  profiletypeid: ProfileTypeId;
  profiletype: ProfileType;
}

export interface UpdatePortalUserPayload {
  user_id: number;
  first_name: string;
  username?: string;
  profile_type_id?: number;
  password?: string;
  last_name?: string;
  location_id?: number | null;
  business_id?: number | null;
  business_ids?: number[];          // MT-831
  referral_group_id?: number | null;
}

export interface AddPortalUserPayload {
  firstname: string;
  username: string;
  profile_type_id: number;
  password: string;
  lastname?: string;
  location_id?: number | null;
  business_id?: number | null;
  business_ids?: number[];          // MT-831
  referral_group_id?: number | null;
}

export interface AddPharmacyUserResponse {
  user_id: number;
  msg: string;
  status: number;
}

export interface UpdatePortalUserResponse {
  msg: string;
  status: number;
}

@Injectable({
  providedIn: 'root',
})
export class JwtAuthService {
  token;
  isAuthenticated: Boolean;
  user: User = {};
  user$ = new BehaviorSubject<User>(this.user);
  signingIn: Boolean;
  return: string;
  JWT_TOKEN = 'JWT_TOKEN';
  APP_USER = 'EGRET_USER';
  DEMO_USER = 'DEMO_USER';
  IS_DEMO_USER_VIEW = 'IS_DEMO_USER_VIEW';
  loginResponse: any;

  constructor(
    private st: SessionStorageService,
    private http: HttpClient,
    private router: Router,
    private route: ActivatedRoute,
    private totp: TotpService,
  ) {
    this.route.queryParams.subscribe(params => {
      this.return = params['return'];
    });
  }

  getDefaultPageUrl() {
    if (this.return) {
      return this.return;
    } else if (this.isDriver()) {
      return 'driver/delivery_pickup';
    } else if (this.isClinician()) {
      return 'orders/online/clinic_to_action';
    } else if (this.isAdmin() || this.isBusiness() || this.isPortalUser()) {
      return 'orders/online/pending_approval';
    } else if (this.isTelehealthPharmacyUser() || this.isClinicalOperationsUser()) {
      return 'telehealth/telehealth-request';
    } else if (this.isRadiology()) {
      return 'radiology/facility-management';
    }
  }

  public signin(username, password) {
    this.signingIn = true;
    return this.http.post(`${environment.apiURL}login`, { username, password }).pipe(
      map((res: any) => {
        if (res.success.is_two_factor) {
          this.loginResponse = res;
          this.router.navigateByUrl('sessions/verify-user');
        } else {
          this.setRoleAndToken(res);
        }
        this.signingIn = false;
        return res;
      }),
      catchError(error => {
        return throwError(error);
      })
    );
  }

  // Field name depends on the challenge method the server issued at login —
  // 'totp' sends { code }, everything else (email) sends { auth_code } as before.
  public verifyUser(code) {
    const body =
      this.loginResponse?.success?.two_factor_method === 'totp'
        ? { code }
        : { auth_code: code };
    return this.http
      .post(
        `${environment.apiURL}verify_tfa`,
        body,
        {
          headers: { Authorization: `Bearer ${this.loginResponse.success.token}` },
        }
      )
      .pipe(
        map((res: any) => {
          this.setRoleAndToken(this.getLoginResponse());
          return res;
        }),
        catchError(error => {
          return throwError(error);
        })
      );
  }

  setRoleAndToken(res: any) {
    const { userid, profiletypeid, firstname, ...details } = res.success.user_details;
    const role = res.success.role;
    // Parse profiletypeid as number (API returns it as string)
    const parsedProfileTypeId = profiletypeid ? parseInt(profiletypeid, 10) : null;
    let user = {
      id: userid,
      profiletypeid: parsedProfileTypeId,
      displayName: firstname,
      details: details,
      businessid: res.success['_business'],
      businessIds: res.success['business_ids'] ?? [],
      businesses: res.success['businesses'] ?? [],
      locationid: res.success['_location'],
      location: res.success['location '],
      role: role,
      referral_group_id: res.success['referral_group_id'],
      referral_group_name: res.success['referral_group_name'],
    };
    this.setUserAndToken(res.success.token, user, !!res);

    // Update Feature Tour user tracking after login
    if ((window as any).FeatureTour?.setExternalRefId) {
      (window as any).FeatureTour.setExternalRefId(userid);
    }
    if ((window as any).FeatureTour?.setProfileTypeId) {
      (window as any).FeatureTour.setProfileTypeId(parsedProfileTypeId);
    }
  }

  setCustomRole(role: UserRole, demoUser: User) {
    this.setIsDemoUserView(true);
    let user = this.getUser();
    if (demoUser) {
      user.role = role;
      user.id = demoUser.id;
      user.displayName = demoUser.displayName;
      this.setUserAndToken(this.getJwtToken(), user, true);
    }
  }

  setDemoClinicianId(id: number) {
    const user = this.getUser();
    const data = { ...user, clinicianId: id };
    this.st.setItem(this.DEMO_USER, data);
  }

  switchToAdminView() {
    this.st.setItem(this.IS_DEMO_USER_VIEW, false);
  }

  setIsDemoUserView(isDemoUserView: boolean) {
    this.st.setItem(this.IS_DEMO_USER_VIEW, isDemoUserView)
  }

  getIsDemoUserView(): boolean {
    return this.st.getItem(this.IS_DEMO_USER_VIEW);
  }

  public resetPassword(data) {
    return (
      this.http
        .post(config.apiUrl + 'forgotpassword', data)
        // return this.http.post(`${environment.apiURL}forgot_password`, data)
        .pipe(
          catchError(error => {
            return throwError(error);
          })
        )
    );
  }

  getUserRole(): Observable<UserRoleResponse> {
    return this.http.get<UserRoleResponse>(config.apiUrl + 'get_user_roles').pipe(
      catchError(error => {
        return throwError(error);
      })
    );
  }

  getRole(): ProfileType {
    return this.getUser().role;
  }

  public checkTokenIsValid() {
    return of(this.getUser()).pipe(
      switchMap((profile: User) => {
        // Call the API method to get user role
        return this.getUserRole().pipe(
          map((userRole: UserRoleResponse) => {
            if (!this.getIsDemoUserView()) {
              profile.role = userRole.profiletype;
              this.setUserAndToken(this.getJwtToken(), profile, true);
              this.signingIn = false;
            } else {
              const user = this.getUser();
              this.setCustomRole(user.role, user);
            }
            return profile;
          })
        );
      }),
      catchError(error => {
        this.signout();
        return of(error);
      })
    );
  }

  get role(): UserRole {
    return this.getUser().role;
  }

  public signout() {
    this.logout();
    // TotpService is root-provided and this method does not reload the page, so
    // its per-user state has to be dropped here or the next account to sign in
    // on this tab inherits it.
    this.totp.clearSessionState();
    this.setUserAndToken(null, null, false);
    if ((window as any).FeatureTour) (window as any).FeatureTour.clearUserId();
    this.router.navigateByUrl('sessions/signin');
  }

  isLoggedIn(): Boolean {
    return !!this.getJwtToken();
  }

  getJwtToken() {
    return this.st.getItem(this.JWT_TOKEN);
  }
  getUser() {
    return this.st.getItem(this.getIsDemoUserView() ? this.DEMO_USER : this.APP_USER) || {};
  }

  getLoginResponse() {
    return this.loginResponse;
  }

  getReferralGroupId() {
    const loginResponse = this.getUser();
    return loginResponse?.referral_group_id || null;
  }

  getReferralGroupName() {
    const loginResponse = this.getUser();
    return loginResponse?.referral_group_name || null;
  }

  setUserAndToken(token: String, user: User, isAuthenticated: Boolean) {
    this.isAuthenticated = isAuthenticated;
    this.token = token;
    this.user = user;
    this.user$.next(user);
    this.st.setItem(this.JWT_TOKEN, token);
    this.st.setItem(this.getIsDemoUserView() ? this.DEMO_USER : this.APP_USER, this.user);
  }
  isAdmin() {
    return this.user?.role === ProfileType.Admin || this.user?.role === ProfileType.SuperAdmin;
  }

  isSuperAdmin() {
    return this.user?.role === ProfileType.SuperAdmin;
  }

  isBusiness() {
    return this.user?.role === ProfileType.BusinessUser || this.user?.role === ProfileType.SuperBusinessUser;
  }

  getBusinessIds(): number[] {
    return this.getUser()?.businessIds ?? [];
  }

  isSuperBusiness() {
    return this.user?.role === ProfileType.SuperBusinessUser;
  }

  isDriver() {
    return this.user?.role === ProfileType.Driver;
  }

  isRadiology() {
    return this.user?.role === ProfileType.RadiologyUser;
  }

  isDoctor() {
    return this.user?.role === ProfileType.Doctor;
  }

  isNursePractitioner() {
    return this.user?.role === ProfileType.NursePractitioner;
  }

  isNurse() {
    return this.user?.role === ProfileType.Nurse;
  }

  isPharmacyUser() {
    return [
      ProfileType.PortalUser,
      ProfileType.TelehealthPharmacy,
      ProfileType.StandardPharmacy,
      ProfileType.ClinicalOperations,
    ].includes(this.user?.role);
  }

  isPortalUser() {
    return this.user?.role === ProfileType.PortalUser;
  }

  isTelehealthPharmacyUser() {
    return this.user?.role === ProfileType.TelehealthPharmacy;
  }

  isClinicalOperationsUser() {
    return this.user?.role === ProfileType.ClinicalOperations;
  }

  isClinician() {
    return [
      ProfileType.Doctor,
      ProfileType.NursePractitioner,
      ProfileType.Nurse,
    ].includes(this.user?.role);
  }

  register(data): Observable<any> {
    return this.http.post(`${config.apiUrl1}register`, data).pipe(
      catchError(err => {
        return throwError(err);
      })
    );
  }

  login(username, type) {
    let data = {
      username: username,
      type: type,
    };
    return this.http.post(`${config.apiUrl1}login`, data).pipe(
      catchError(err => {
        return throwError(err);
      })
    );
  }

  logout() {
    this.http
      .post(`${config.apiUrl}logout`, null)
      .pipe(catchError(err => throwError(err)))
      .subscribe();
  }

  referUser(data): Observable<any> {
    return this.http.post(`${config.apiUrl1}referUser`, data).pipe(
      catchError(err => {
        return throwError(err);
      })
    );
  }

  addPharmacyUser(data: AddPortalUserPayload) {
    return this.http
      .post<AddPharmacyUserResponse>(`${config.apiUrl}addPharmacyUser`, data)
      .pipe(
        catchError(err => {
          return throwError(err);
        })
      );
  }

  updatePortalUser(data: UpdatePortalUserPayload) {
    return this.http
      .put<UpdatePortalUserResponse>(
        `${config.apiUrl1}update_portal_user/${data.user_id}`,
        data
      )
      .pipe(
        catchError(err => {
          return throwError(err);
        })
      );
  }
}
